import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  createTheme,
  CssBaseline,
  ThemeProvider,
} from "@mui/material";
import Swal from "sweetalert2";
import AddComplaint from "./AddComplaint";
import EditComplaint from "./EditComplaint";
import ComplaintsTable from "./ComplaintsTable";
import { useLocation, useNavigate } from "react-router-dom";

const ComplaintsPage = ({ user: propUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const user = propUser || location.state?.user || null;

  const theme = createTheme({
    palette: {
      mode: "dark",
      background: {
        default: "#121212",
        paper: "#1e1e1e",
      },
      primary: {
        main: "#6FD943",
      },
      secondary: {
        main: "#d33",
      },
      text: {
        primary: "#ddd",
        secondary: "#bbb",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            margin: 0,
            padding: 0,
            width: "100%",
            height: "100%",
            overflowX: "hidden",
          },
        },
      },
    },
  });

  useEffect(() => {
    if (!user) {
      console.warn("No user data found, redirecting...");
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    Swal.fire({
      title: "تنبيه أمني",
      text: "ف حالة انك لا تود ذكر اسمك ؟ لا مشكله فهذه الصفحه سريه للغايه",
      icon: "warning",
      confirmButtonText: "موافق",
      confirmButtonColor: "#d33",
    });

    const fetchComplaints = async () => {
      try {
        const response = await fetch(
          `https://backenderpforseg.samielmetwali.com/problems_user/get_problem_user.php?employee=${user.name}`
        );
        const data = await response.json();
        if (Array.isArray(data)) {
          setComplaints(data);
        } else {
          setComplaints([]); // تعيين مصفوفة فارغة في حالة حدوث خطأ
          console.error("البيانات المستلمة ليست مصفوفة:", data);
        }
      } catch (error) {
        setComplaints([]);
        console.error("خطأ أثناء تحميل الشكاوى:", error);
      }
    };

    fetchComplaints();
  }, [user.name]);

  const handleAddComplaint = async (data) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        "https://backenderpforseg.samielmetwali.com/problems_user/add_problems.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            employee: user.name,
            department: user.department2,
            company: user.department,
            ...data,
          }),
        }
      );

      const result = await response.json();
      if (result.message) {
        setComplaints((prev) => [
          ...prev,
          {
            id: complaints.length + 1,
            ...data,
            status: "قيد المراجعة",
            answer: "لم يتم الرد",
          },
        ]);
        setDialogOpen(false);
        Swal.fire("نجاح", "تم إرسال الشكوى بنجاح!", "success");
      }
    } catch (error) {
      Swal.fire("خطأ", "حدث خطأ أثناء الإرسال!", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateComplaint = async (id, updatedData) => {
    try {
      const response = await fetch(
        "https://backenderpforseg.samielmetwali.com/problems_user/update_problem_user.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, ...updatedData }),
        }
      );

      const result = await response.json();
      if (result) {
        setComplaints((prev) =>
          prev.map((complaint) =>
            complaint.id === id ? { ...complaint, ...updatedData } : complaint
          )
        );
        setEditDialogOpen(false);
        Swal.fire("نجاح", "تم التحديث بنجاح!", "success");
      }
    } catch (error) {
      Swal.fire("خطأ", "حدث خطأ أثناء التحديث!", "error");
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "هل أنت متأكد؟",
      text: "لن تتمكن من استعادة هذه الشكوى!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "نعم، احذفها!",
      cancelButtonText: "إلغاء",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await fetch(
            "https://backenderpforseg.samielmetwali.com/problems_user/delete_problem.php",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id }),
            }
          );
          setComplaints((prev) => prev.filter((c) => c.id !== id));
          Swal.fire("تم الحذف!", "تم حذف الشكوى بنجاح.", "success");
        } catch (error) {
          Swal.fire("خطأ!", "حدث خطأ أثناء الحذف!", "error");
        }
      }
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          p: 4,
          m: 0,
          direction: "rtl",
          backgroundColor: "#121212",
          minHeight: "100vh",
          color: "#ddd",
        }}
      >
        <Button
          variant="contained"
          onClick={() => setDialogOpen(true)}
          sx={{
            bgcolor: "#6FD943",
            color: "#fff",
            fontWeight: "bold",
            mb: 4,
            "&:hover": { bgcolor: "#57B32F" },
          }}
        >
          إضافة شكوى جديدة
        </Button>

        <AddComplaint
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleAddComplaint}
          loading={isLoading}
          user={user}
        />

        <EditComplaint
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSubmit={handleUpdateComplaint}
          complaint={selectedComplaint}
        />

        <ComplaintsTable
          complaints={complaints}
          onEdit={(complaint) => {
            setSelectedComplaint(complaint);
            setEditDialogOpen(true);
          }}
          handleDelete={handleDelete}
          isDarkMode={true} // Always force dark mode
        />
      </Box>
    </ThemeProvider>
  );
};

export default ComplaintsPage;
